import { ScrollDepthPageName } from '@services/scroll-depth-service/scroll-depth.types';

export interface RegimenRehaulV1Variant {
  back_nudge_dr_call?: boolean;
  dr_banner_change?: boolean;
  remove_variant?: boolean;
}

export interface RegimenRehaulV1Experiment {
  key?: string;
  variant: RegimenRehaulV1Variant;
}

export interface RegimenRehaulV1Result {
  nudgeDoctorCallOnBack?: boolean;
  drBannerChange?: boolean;
  removeVariants?: boolean;
}

export interface RegimenPreparationTimerVariant {
  activationDelayInSeconds: number;
}

export interface RestrictedProductForPregnancyAndTeenagerExperiment extends Record<string, unknown> {
  variant: {
    pregnancy: boolean;
    teenager: boolean;
    approval: boolean;
    PREGNANT: string[];
    NEW_MOM: string[];
    PLANNING_FOR_PREGNANCY: string[];
    teenager_safety_profile: string[];
  };
  enable: boolean;
}

export interface ReviewOrderPageExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    shouldShowShiprocket: boolean,
  };
}

export interface RegimenReorderExperiment extends Record<string, unknown> {
  variant: {
    bannerVisibilityPhases: { startDay: number; endDay: number; hideIfOrderPlaced: boolean }[];
    minFollowupCount: number;
    minDiscountOnOrder: number;
    minRegimenProducts: number;
    maxRegimenProducts: number;
    priceSlab: { productCount: number; prices: Record<string, number> }[];
    showDoctorCharges?: boolean;
  };
  enable: boolean;
}

export interface RegimenPricingExperiment extends Record<string,unknown> {
  enabled: boolean;
  variant;
}

export interface BottomNavConfigExperiment {
  enable: boolean;
  variant: {
    acq: string[];
    ret: string[];
  };
}

export interface ShopDynamicBannerItem {
  imageUrl: string;
  navigationUrl?: string;
}

export interface ShopDynamicBannerHeader {
  en?: string;
  hi?: string;
  te?: string;
  ml?: string;
  mr?: string;
  ta?: string;
  kn?: string;
  bn?: string;
}

export interface ShopDynamicBannerExperiment extends Record<string, unknown> {
  variant: {
    banners: ShopDynamicBannerItem[];
    header?: ShopDynamicBannerHeader;
    backgroundColor?: string;
  };
  enable: boolean;
}

export interface ChatSkipLastQuestionsExperiment extends Record<string, unknown> {
  variant: {
    questionIdsToSkipAt: string[];
    concernClasses?: string[];
  };
  enable: boolean;
}

export interface RecentlyViewedExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    SHOP: boolean;
    HOME: boolean;
  };
}

export interface RegimenProductImageCarouselExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    FACE: boolean;
    HAIR: boolean;
  };
}

export type RegimenColor = 'teal' | 'orange' | 'blue';

export const REGIMEN_VARIANT_COLOR_EXPERIMENT_KEY = 'regimen_variant_color';

export interface RegimenVariantColorExperiment extends Record<string, unknown> {
  enable?: boolean;
  variant?: Partial<Record<'1499' | '1999' | '4999', RegimenColor>>;
}

export enum RecommendedProductsSurface {
  Home = 'home',
  Shop = 'shop',
}

export interface RecommendedProductsExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    'home-start-day': number;
    'home-end-day': number;
    'shop-start-day': number;
    'shop-end-day': number;
  };
}

export interface RetentionHomeRevampExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    compactBannerVisibleAfterDays: number;
    order_delivery_banner_partial_videos?: boolean;
    order_delivery_banner_start_day?: number;
    order_delivery_banner_end_day?: number;
  };
}

export interface FollowUpChatDefaultFirstAnswerExperiment extends Record<string, unknown> {
  enable: boolean;
  variant?: Record<string, unknown>;
}

export interface PrepaidDiscountVariant {
  couponCode: string;
  shipRocket?: boolean;
}

export interface PrepaidDiscountExperiment extends Record<string, unknown> {
  variant: PrepaidDiscountVariant;
  enable: boolean;
}

export interface FupQuestionsNewUiExperiment extends Record<string, unknown> {
  enable: boolean;
  variant?: {
    skipQuestion?: string[];
  };
};

export interface RegimenVariantsScrollViewCollapseExperiment {
  hideAfterTopScroll: boolean;
}

/** One female age band: applies when the user's age is <= maxAge (bands are evaluated in order). */
export interface QuizStepBand {
  maxAge: number;
  count: number;
}

/** Fixed quiz step totals per gender for a class (keys mirror ApiClientConstant.User.Gender values). */
export interface QuizStepCounts {
  Male: number;
  Female: QuizStepBand[];
}

/** Fibonacci bot-reply poll seed (ms), same t1/t2 shape as chatV2's chat_response_time variant. */
export interface QuizBotReplyDelay {
  t1: number;
  t2: number;
}

export interface QuizQuestionEntry {
  questionId: string;
  gate?: string;
}

/** Per-gender ordered question lists for one concern class. */
export interface QuizQuestionEntries {
  Male: QuizQuestionEntry[];
  Female: QuizQuestionEntry[];
}

export interface ConsultationQuizExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    classes?: string[];
    /** Per-class fixed question counts for the quiz progress header (retunable without a release). */
    stepCounts?: Record<string, QuizStepCounts>;
    /** Overrides the quiz bot-reply poll seed; omit to keep the default 1000/1000 (2s first poll). */
    botReplyDelay?: QuizBotReplyDelay;
    questionIds?: Record<string, QuizQuestionEntries>;
    /** Question ids whose option list renders as a 2-up grid; omit to keep the code defaults. */
    gridQuestionIds?: string[];
    /** Window the photo gates treat an InstantCheckup as recent, matching the server's tree rule. */
    instantCheckupRecencyDays?: number;
    /** Hides the N/M step count in the quiz header. The progress bar itself is unaffected. */
    hideProgressCount?: boolean;
    triggerAtQuestionId?: string;
    singleStepBack?: boolean;
    mandatoryHairTopPhotoBeforeQuestionId?: string;
    showConcernSpecificQuestionFirst?: boolean;
    skipPhotoInstructions?: boolean;
    photoUploadedToastQuestionIds?: Record<string, string[]>;
  };
}

/** What the money_back_guarantee variant configures for one regimen class. */
export interface MoneyBackGuaranteeClassConfig {
  variantIds: string[];
  shouldShowOnReviewOrderPage: boolean;
}

export interface MoneyBackGuaranteeExperiment extends Record<string, unknown> {
  enable: boolean;
  variant?: {
    FACE?: string[];
    HAIR?: string[];
    pageImages?: Record<string, string[]>;
    regimen_review_page_skin?: boolean;
    regimen_review_page_hair?: boolean;
  };
}

// scroll_depth_trigger: gates the scroll_depth analytics event.
// variant.pageNames missing/empty = fire on all pages; non-empty = only listed pageNames.
export interface ScrollDepthTriggerExperiment extends Record<string, unknown> {
  variant?: {
    pageNames?: ScrollDepthPageName[];
  };
}

export interface HairAiExperiment extends Record<string, unknown> {
  enable: boolean;
  variant?: Record<string, string[] | { values?: string[] }>;
}

export interface AiReportHairExperiment extends Record<string, unknown> {
  enable?: boolean;
  variant?: {
    triggerAtQuestionId?: string;
  };
}

export interface UserProfileInputBreakdownExperiment extends Record<string, unknown> {
  enable: boolean;
  variant?: {
    /** true = tapping a gender routes straight to the next page; false = reveal a Continue CTA instead. */
    skip_gender_cta?: boolean;
    /** true = never show the age page; Age is left uncollected and stops gating onboarding. */
    skip_age_page?: boolean;
  };
}

/** One "People also ask" FAQ entry for the unpaid-user LLM chat bottomsheet. */
export interface UnpaidUserLlmBotQA {
  englishQuestion: string;
  hindiQuestion: string;
  questionLanguageString: string;
  answerLanguageString: string;
}

// unpaid_user_llm_bot: languages/pagesToShow gate the banner surfaces; defaultQAs overrides the bottomsheet FAQ chips.
export interface UnpaidUserLlmBotExperiment extends Record<string, unknown> {
  enable?: boolean;
  variant?: {
    languages?: string[];
    pagesToShow?: string[];
    defaultQAs?: UnpaidUserLlmBotQA[];
  };
}

/** Configured FAQ chip for the paid AI help landing page. */
export interface PaidAiChatQA {
  englishQuestion: string;
  hindiQuestion?: string;
  questionLanguageString?: string;
}

/** `enable_paid_ai_chat` variant configuration. */
export interface PaidAiChatExperiment extends Record<string, unknown> {
  enable?: boolean;
  variant?: {
    languages?: string[];
    pagesToShow?: string[];
    defaultQAs?: PaidAiChatQA[];
  };
}

/** `new_help_ui_v2` configuration for the paid Help landing page and FAQ chips. */
export interface NewHelpUiV2Experiment extends PaidAiChatExperiment {}

export interface InvestigationBannerExperiment extends Record<string, unknown> {
  enable: boolean;
  variant?: {
    startDay: number;
    endDay: number;
  };
}

export interface AppIconExperiment extends Record<string, unknown> {
  enable?: boolean;
  variant?: {
    allocatedStartDay: number;
    allocatedEndDay: number;
    expiringStartDay: number;
    expiringEndDay: number;
  };
}

export interface CrossSellRegimenExperiment extends Record<string, unknown> {
  enable?: boolean;
  variant?: {
    FACE: {
      start_time: string;
      end_time: string;
      discount: number;
    };
    HAIR: {
      start_time: string;
      end_time: string;
      discount: number;
    };
    paymentPageDiscountDisclaimer: string;
    startDay: number;
    endDay: number;
  };
}

export enum AddressPageLocationFlow {
  InlineForm = 'inlineForm',
  BottomSheet = 'bottomSheet',
}

export interface AutoFillAddressWithCoordinatesExperiment extends Record<string, unknown> {
  enable?: boolean;
  variant?: {
    /** Location CTA on the new-address page; the value decides where the remaining details are filled. Absent = no CTA. */
    addressPageLocationFlow?: AddressPageLocationFlow;
    /** Location CTA on the review-order page (no default address). */
    showLocationCtaOnReviewOrder?: boolean;
  };
}
